import { appRouteOf, parseAddress, workAreaRoutePath } from '@featit/shared/addressing/paths';
import { workAreaRouteRef } from '@featit/shared/addressing/workAreaRouteRef';
import { type IProject } from '@featit/shared/types/index';
import {
  isNamespaceScopedPath,
  WORK_AREAS_ACTIVITY_PATH,
  WORK_AREAS_DECISIONS_PATH,
  WORK_AREAS_DOCUMENTS_PATH,
  WORK_AREAS_HOME_PATH,
  WORK_AREAS_ITEMS_PATH,
  WORK_AREAS_SCHEDULES_PATH,
  WORK_AREAS_TASKS_PATH,
  WORK_AREAS_VIEWS_PATH,
  isWorkAreaOverviewPath,
} from './workAreaOverviewUrls';
import {
  projectActivityPath,
  projectHomePath,
  projectItemsPath,
  projectRoadmapPath,
  projectSettingsPath,
  projectViewsPath,
} from '@/features/work-items/projectItemUrls';
import { projectDocumentsPath } from '@/features/documents/documentUrls';
import { APP_HOME_PATH } from '@/routing/appHome';
import { viewSegments } from '@/utils/documentTitle';
import { projectSchedulesPath } from '@/features/schedules/scheduleUrls';

/**
 * Which views can show every work area at once, and what happens to the rest when a reader chooses
 * "All work areas" (I-0405).
 *
 * ⭐ Measured on 271b3fae: the scope was a URL family (`/-/work-areas/*`), so choosing All always
 * navigated to that family's Home, and any view outside it — Agents among them — read the last
 * selection back. The table below is the whole decision, one entry per view.
 *
 * ⛔ **A view that needs one work area is never redirected silently.** It gets an explicit choice
 * (`/-/work-areas/choose/<view>`), which names the view and lists the work areas to open it in.
 */
export const SINGLE_WORK_AREA_VIEWS = {
  items: {
    label: 'Work items',
    i18n: { label: 'allWorkAreas.choose.views.items' },
    pathFor: projectItemsPath,
  },
  roadmap: {
    label: 'Roadmap',
    i18n: { label: 'allWorkAreas.choose.views.roadmap' },
    pathFor: projectRoadmapPath,
  },
  settings: {
    label: 'Work area settings',
    i18n: { label: 'allWorkAreas.choose.views.settings' },
    pathFor: projectSettingsPath,
  },
  pages: {
    label: 'Pages',
    i18n: { label: 'allWorkAreas.choose.views.pages' },
    pathFor: projectHomePath,
  },
  // F-0424: a namespace's Schedules asks which work area; All work areas has its own list (U-0065).
  schedules: {
    label: 'Schedules',
    i18n: { label: 'allWorkAreas.choose.views.schedules' },
    pathFor: projectSchedulesPath,
  },
} as const;

export type SingleWorkAreaViewType = keyof typeof SINGLE_WORK_AREA_VIEWS;

export const WORK_AREAS_CHOOSE_PATH = `${WORK_AREAS_HOME_PATH}/choose`;

export function chooseWorkAreaPath(view: SingleWorkAreaViewType): string {
  return `${WORK_AREAS_CHOOSE_PATH}/${view}`;
}

export function isSingleWorkAreaView(value: string | undefined): value is SingleWorkAreaViewType {
  return Boolean(value && Object.prototype.hasOwnProperty.call(SINGLE_WORK_AREA_VIEWS, value));
}

/** Where a reader goes when they choose All work areas while on `pathname`. `null` means stay. */
export function pathForAllWorkAreas(pathname: string): string | null {
  // Views about the whole account, or already about every work area: they support the scope as-is.
  if (isWorkAreaOverviewPath(pathname)) return null;
  const address = parseAddress(pathname);
  const appRoute = address.kind === 'app' ? address.route : null;
  if (appRoute && /^\/(agents|infrastructure|settings|admin|profile)(\/|$)/.test(appRoute)) {
    return null;
  }

  const rest = address.kind === 'app' || address.kind === 'work-area' ? address.route : pathname;
  /*
    F-0383, §10.9: a namespace-scoped view widens to All work areas by what comes AFTER the
    namespace segment, the same way a work area's own route widens by what comes after it — not by
    the namespace segment itself, which every one of the checks below would otherwise miss.
  */
  const namespaceMatch = rest.match(/^\/namespaces\/[^/]+(\/.*)?$/);
  const widened = namespaceMatch ? (namespaceMatch[1] ?? '') : rest;

  if (/^\/activity(\/|$)/.test(widened)) return WORK_AREAS_ACTIVITY_PATH;
  if (/^\/views(\/|$)/.test(widened)) return WORK_AREAS_VIEWS_PATH;
  if (/^\/documents(\/|$)/.test(widened)) return WORK_AREAS_DOCUMENTS_PATH;
  // F-0547: Work items has an All work areas list, so widening keeps the page instead of asking.
  if (/^\/items(\/|$)/.test(widened)) return WORK_AREAS_ITEMS_PATH;
  // I-0560: *Decisions for me* is the surface a reader widens to when they want everything, so it
  // has an All work areas twin like Activity, Views and Documents rather than vanishing.
  if (/^\/decisions(\/|$)/.test(widened)) return WORK_AREAS_DECISIONS_PATH;
  // I-0900: "Tasks for me" is present under both scopes exactly like Decisions, directly below it.
  if (/^\/tasks(\/|$)/.test(widened)) return WORK_AREAS_TASKS_PATH;
  // U-0065: Schedules has an All work areas list, so widening keeps the page (Jay: "I can't select
  // all workareas in the schedules view": the app's switcher stayed on one work area's list).
  if (/^\/schedules(\/|$)/.test(widened)) return WORK_AREAS_SCHEDULES_PATH;
  if (widened === '' || widened === '/' || widened === '/home') return WORK_AREAS_HOME_PATH;

  const single = singleWorkAreaViewForPath(pathname);
  return single ? chooseWorkAreaPath(single) : WORK_AREAS_HOME_PATH;
}

/**
 * The single-work-area view a path is, when it is one. Used for the unscoped routes (`/-/items`,
 * `/-/roadmap`, `/-/home`'s pages) which read their work area from the selection: under the All scope they
 * would silently open the remembered work area, so the shell shows the choice instead.
 */
export function singleWorkAreaViewForPath(pathname: string): SingleWorkAreaViewType | null {
  const address = parseAddress(pathname);
  const scoped = address.kind === 'work-area';
  const rest = address.kind === 'app' || address.kind === 'work-area' ? address.route : '';
  if (/^\/items(\/|$)/.test(rest)) return 'items';
  if (/^\/roadmap(\/|$)/.test(rest)) return 'roadmap';
  if (/^\/settings(\/|$)/.test(rest) && scoped) return 'settings';
  if (/^\/pages\//.test(rest)) return 'pages';
  return null;
}

/** An unscoped app route (no work area in the address) that reads its work area from the selection. */
export function isUnscopedSingleWorkAreaRoute(pathname: string): boolean {
  const route = appRouteOf(pathname);
  return route !== null && /^\/(items|roadmap)(\/|$)/.test(route);
}

/**
 * Whether the All scope's choice is still waiting on the work-area list: it has not answered, and has not
 * failed with nothing known. The one rule `allScopeTargetFor` returns `'pending'` by, asked alone by what only
 * needs to know whether a page is drawing that wait (the sidebar's switcher), so it reads no list.
 */
export function allScopeIsPending(answered: boolean, failed: boolean): boolean {
  return !answered && !failed;
}

/**
 * I-1118: where an unscoped single-work-area route (`/-/items`, `/-/roadmap`) goes under the All
 * scope. `'pending'` while the work-area list has not answered: the choice is never made before the
 * list is known (on a phone, Items opened "Choose a work area" on 2 of 3 launches for a person with
 * one work area). The work area last opened opens; with none remembered, the only one there is;
 * otherwise the explicit choice (I-0405, as amended), and the choice too when the one last opened is
 * gone, however many are left (I-2168).
 *
 * A list that failed with nothing known keeps the choice page, which says so — never a wait for an
 * answer that is not coming.
 */
export function allScopeTargetFor(
  pathname: string,
  projects: readonly Pick<IProject, 'id' | 'slug' | 'namespace'>[],
  answered: boolean,
  failed: boolean,
  lastOpenedId: string | null = null,
): 'pending' | string {
  const view = singleWorkAreaViewForPath(pathname) ?? 'items';
  if (allScopeIsPending(answered, failed)) return 'pending';
  /*
    The captain's ruling on I-1118 (2026-10-05), amending I-0405: the work area last opened is
    opened, its name in the top row the one-tap switch; the choice shows only when nothing is
    remembered and there are several.
  */
  const lastOpened = lastOpenedId ? projects.find((project) => project.id === lastOpenedId) : null;
  /*
    I-2168 (MWI-27): the work area last opened went away. With one left, opening it is a choice the
    person did not make: its address names it, and the scope follows (`enterWorkArea`) — the silent
    move into another work area the captain's ruling of 2026-10-22 forbids. They choose, as with two.
  */
  const lastOpenedGone = Boolean(lastOpenedId) && !lastOpened;
  const opened = lastOpened ?? (projects.length === 1 && !lastOpenedGone ? projects[0] : null);
  if (opened) return SINGLE_WORK_AREA_VIEWS[view].pathFor(workAreaRouteRef(opened));
  return chooseWorkAreaPath(view);
}

/**
 * I-2216 (AC-19.21.1, as amended): where the app's home (`/-/items`, where `/` sends a reader) goes
 * in a browser and the desktop app. An address that names no work area is the All scope (Jay,
 * 2026-10-27: "when [the server] is opened - i.e. the server without any work area selection it
 * should default to all work areas"): with more than one work area this person is a member of, All
 * work areas (`WORK_AREAS_HOME_PATH`), whatever this device remembers. Neither the work area last
 * opened (I-1118) nor the first of the list, which is only what a load falls back to (I-1992), is a
 * choice this address made. `'pending'` until the list answers, so neither one's page is drawn (or
 * its address written) first; `null` is no change.
 *
 * An address that names a work area keeps it: only the home itself, never `/-/items/<key>`,
 * `/-/items/new` or a work area's own Items. Not in the iPhone and iPad app (`inAppShell`), whose
 * Items tab is a tab's root (I-1118); not for a guest everywhere, who starts on Shared with you
 * (`landsOnSharedWithYou`); and not with one work area, which Items opens.
 */
export function appHomeTargetFor({
  pathname,
  answered,
  failed,
  memberWorkAreaCount,
  inAppShell,
}: {
  pathname: string;
  answered: boolean;
  failed: boolean;
  memberWorkAreaCount: number;
  inAppShell: boolean;
}): 'pending' | string | null {
  // I-2554 (AC-19.21.1a): `/` is the home too, and goes where the home would in one replacement,
  // never `/` → `/-/items` → `/-/work-areas`. Where the home stays itself, `/` goes to it.
  const atRoot = pathname === '/';
  const stays = atRoot ? APP_HOME_PATH : null;
  if (inAppShell) return stays;
  if (!atRoot && appRouteOf(pathname) !== '/items') return null;
  if (!answered) return failed ? stays : 'pending';
  return memberWorkAreaCount > 1 ? WORK_AREAS_HOME_PATH : stays;
}

/**
 * I-0958 WS-6: work-area pages whose own list a switch opens in the new work area, by the same
 * segment (`/<work area>/-/decisions`, `…/tasks`, `…/parts`).
 */
const SAME_PAGE_IN_ANOTHER_WORK_AREA = new Set(['decisions', 'tasks', 'parts', 'schedules']);

/** A switch keeps the page's own query, but not one entry of it (`focus`) or a view's id. */
function projectSwitchSearch(search: string): string {
  const params = new URLSearchParams(search);
  params.delete('focus');
  params.delete('view');
  const next = params.toString();
  return next ? `?${next}` : '';
}

/**
 * Where the reader goes when the work area `routeRef` is chosen while `pathname` is on screen: the
 * same page in that work area, its list rather than one of its entries; `null` means stay. The one
 * rule for a switch made in the web's own picker (`Layout`'s `handleProjectSelect`) and for a switch
 * made anywhere else in the app (I-1363, `screenFollowTarget`), so the two cannot differ.
 */
export function pathForWorkAreaSwitch(
  pathname: string,
  search: string,
  routeRef: string,
): string | null {
  // By segment, never by substring: a slug such as `items-backlog` is not the items view (I-0385).
  // I-1089: a namespace's page (`/namespaces/<id>/<view>`) carries over as that view; its Home as
  // the work area's Home (below).
  const [view] = viewSegments(pathname);
  if (view === 'items') return `${projectItemsPath(routeRef)}${projectSwitchSearch(search)}`;
  if (view === 'roadmap') return projectRoadmapPath(routeRef);
  if (view === 'activity') return projectActivityPath(routeRef);
  if (view === 'views') return projectViewsPath(routeRef);
  if (view === 'documents') return projectDocumentsPath(routeRef);
  if (view === 'settings') return `${projectSettingsPath(routeRef)}${projectSwitchSearch(search)}`;
  if (view === 'pages') return projectHomePath(routeRef);
  if (view && SAME_PAGE_IN_ANOTHER_WORK_AREA.has(view)) {
    return workAreaRoutePath(routeRef, `/${view}`);
  }
  const here = parseAddress(pathname);
  const appRoute = appRouteOf(pathname);
  if (
    (here.kind === 'work-area' && here.route === '') ||
    appRoute === '/home' ||
    // I-1074: a work area's row picked from the More tab opens that work area's Home.
    appRoute === '/more'
  ) {
    return projectHomePath(routeRef);
  }
  if (pathname.startsWith(`${WORK_AREAS_CHOOSE_PATH}/`)) {
    // The reader was asked to choose a work area for a view; choosing one opens that view there.
    const chosen = pathname.slice(WORK_AREAS_CHOOSE_PATH.length + 1);
    return isSingleWorkAreaView(chosen)
      ? SINGLE_WORK_AREA_VIEWS[chosen].pathFor(routeRef)
      : projectHomePath(routeRef);
  }
  if (isWorkAreaOverviewPath(pathname) || isNamespaceScopedPath(pathname)) {
    return projectHomePath(routeRef);
  }
  return null;
}
